<script setup>
import { ref, watch, onMounted, nextTick, computed, toRaw } from 'vue';
import { useI18n } from 'vue-i18n';
import EnButton from 'Components/base/EnButton/EnButton.vue';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogOverlay,
  DialogPortal,
  DialogTrigger,
} from 'Components/primitives/dialog';
import EnForm from 'Components/inputs/EnForm/EnForm.vue';
import { toTypedSchema } from '@vee-validate/zod';
import { cn } from 'Components/primitives/utils';
import { X as CloseIcon } from 'lucide-vue-next';
import { useDialogStyles } from './useDialogStyles';
import { cloneDeep } from 'Js/utils/clone';
import { ConfigProvider } from 'reka-ui';

const { t } = useI18n();

const props = defineProps({
  /**
   * The text displayed as the header of the dialog.
   */
  title: String,
  /**
   * Specifies whether the small style is applied (true) or not (false).
   */
  small: {
    type: Boolean,
    default: false
  },
  /**
   * Specifies whether the dialog fits the content (true) or not (false).
   */
  autoWidth: Boolean,
  /**
   * Specifies the width of the dialog.
   */
  width: [String, Number],
  /**
   * Specifies the min width of the dialog.
   */
  minWidth: [String, Number],
  /**
   * Specifies the max width of the dialog.
   */
  maxWidth: [String, Number],
  /**
   * Specifies the height of the dialog.
   * If not specified, the dialog will take the default height.
   */
  height: [String, Number],
  /**
   * Specifies the min height of the dialog.
   */
  minHeight: [String, Number],
  /**
   * Specifies the max height of the dialog.
   */
  maxHeight: [String, Number],
  /**
   * Specifies whether the dialog actions are fixed (true) or not (false).
   */
  fixedActions: Boolean,
  /**
   * Specifies whether the close icon is shown (true) or not (false).
   * Defaults to true.
   */
  closeIcon: {
    type: Boolean,
    default: true,
  },
  /**
   * If passed this method is called before the dialog is closed.
   * If true is returned the dialog is closed.
   * If false is returned the dialog is not closed.
   */
  confirmClose: Function,
  /**
   * Specifies whether clicking outside of the element or pressing esc key will not deactivate it.
   * Defaults to true.
   */
  persistent: {
    type: Boolean,
    default: true,
  },
  /**
   * Specifies the static text displayed as a message.
   */
  text: String,
  /**
   * When true, the content border will be hidden.
   */
  hideContentBorder: {
    type: Boolean,
    default: false,
  },
  /**
   * When true, header and footer get a bit more space to the dialog edges.
   */
  spacious: {
    type: Boolean,
    default: false,
  },
  /**
   * Validation schema for the form (vee-validate or zod).
   */
  validationSchema: Object,
  /**
   * Initial values for the form.
   */
  initialValues: Object,
  /**
   * Validate fields on mount.
   */
  validateOnMount: {
    type: Boolean,
    default: false,
  },
  /**
   * Specifies whether the dialog is modal.
   */
  modal: {
    type: Boolean,
    default: true,
  },
  staticId: {
    type: String,
  },
  zIndex: {
    type: [Number, String],
    default: 2010,
  },
});

const model = defineModel({ type: Boolean, default: false });

const emit = defineEmits(['close', 'submit']);

const dialogContentRef = ref(null);
const hasScroll = ref(false);

const checkScroll = () => {
  const el = dialogContentRef.value;
  if (!el) return;
  hasScroll.value = el.scrollHeight > el.clientHeight + 1;
};

async function requestClose() {
  if (typeof props.confirmClose === 'function') {
    try {
      const ok = await props.confirmClose();
      if (ok === false) return;
    } catch {
      return;
    }
  }

  model.value = false;
  emit('close');
}

const handleOpenChange = (open) => {
  if (!open) {
    requestClose();
  } else {
    model.value = true;
  }
};
/**
 * Get the validations schema from props.
 * @type {ComputedRef<TypedSchema<PartialDeep<input<*>>, output<*>>|*>}
 */
const typedSchema = computed(() => {
  if (props.validationSchema && typeof props.validationSchema.parse === 'function') {
    return toTypedSchema(props.validationSchema);
  }
  return props.validationSchema;
});

// vee-validate `<Form>` deep-watches `initialValues` and may re-initialize when it changes.
// Callers often pass reactive objects (e.g. live edit models). To prevent feedback loops
// (e.g. `Maximum recursive updates exceeded in component <Form>`), snapshot the initial
// values when the dialog opens and keep them stable while it stays open.
const formInitialValues = ref(props.initialValues ? cloneDeep(toRaw(props.initialValues)) : undefined);

watch(
    () => model.value,
    (open) => {
      if (!open) return;
      formInitialValues.value = props.initialValues ? cloneDeep(toRaw(props.initialValues)) : undefined;
    },
);

watch(
    () => props.initialValues,
    (val) => {
      // If the dialog is closed, allow the next open to start with the latest initial values.
      if (model.value) return;
      formInitialValues.value = val ? cloneDeep(toRaw(val)) : undefined;
    },
);

const { dialogStyle, contentClass } = useDialogStyles(props);

function buildIdForCloseButton(isX) {
  let prefix = '';
  if(props.staticId) {
    prefix = props.staticId;
  }
  return `${prefix}-${isX ? 'x' : 'close'}`;
}

function onCloseAutoFocus(event) {
  event.preventDefault();
  // move focus somewhere that does not have a tooltip
  document.body.focus?.();
}

onMounted(async () => {
  await nextTick();
  checkScroll();
});

watch(() => model.value, async (open) => {
  if (!open) return;
  await nextTick();
  checkScroll();
});
</script>

<template>
  <config-provider :scroll-body="false">
    <Dialog :open="model" @update:open="handleOpenChange" :modal="modal">
      <dialog-trigger v-if="$slots.activator" as-child>
        <slot name="activator"></slot>
      </dialog-trigger>

      <dialog-portal>
        <dialog-overlay :style="{ zIndex: zIndex }" class="en-dialog-overlay"></dialog-overlay>
        <dialog-content
            :class="cn('en-dialog-content p-0 flex flex-col overflow-hidden rounded-lg max-h-[90vh]', contentClass)"
            :style="{ ...dialogStyle, zIndex: zIndex }"
            :aria-describedby="undefined"
            @pointer-down-outside="(e) => { if (persistent) e.preventDefault() }"
            @escape-key-down="(e) => { if (persistent) e.preventDefault() }"
            @close-auto-focus="onCloseAutoFocus"
        >
          <en-form
              :validation-schema="typedSchema"
              :initial-values="formInitialValues"
              :validate-on-mount="validateOnMount"
              @submit="(values, actions) => emit('submit', values, actions)"
              class="flex flex-col grow overflow-hidden"
              v-slot="formContext"
          >
            <div
                :class="[
              (hasScroll ? 'has-scroll' : 'no-scroll'),
              'dialog-content-container',
              (props.hideContentBorder ? 'no-top-border' : ''),
              (props.spacious ? 'spacious' : '')
            ]"
            >
              <div class="top-section">
                <div class="title-row">
                  <dialog-title class="title">
                    <slot name="title">{{ title }}</slot>
                  </dialog-title>
                  <div class="header-actions">
                    <slot name="header-actions"></slot>
                    <en-button variant="ghost" v-if="closeIcon" class="close-icon-container" :static-id="buildIdForCloseButton(true)" @click="requestClose">
                      <close-icon :size="16"></close-icon>
                    </en-button>
                  </div>
                </div>
                <slot name="header-subtitle"></slot>
              </div>

              <div class="content-area">
                <div ref="dialogContentRef" class="content" @scroll="checkScroll">
                  <slot name="content-prepend" v-bind="formContext"></slot>

                  <slot name="content" v-bind="formContext">
                    {{ text }}
                  </slot>

                  <slot name="content-append" v-bind="formContext"></slot>
                </div>
                <slot name="overlay"></slot>
              </div>

              <div class="actions-section">
                <slot :close="requestClose" name="actions" v-bind="formContext">
                  <en-button :static-id="buildIdForCloseButton()" size="xs" @click="requestClose">{{ t('common.buttons.close') }}</en-button>
                </slot>
              </div>
            </div>
          </en-form>
        </dialog-content>
      </dialog-portal>
    </Dialog>
  </config-provider>
</template>

<style lang="scss" scoped>
@use "Styles/helper/variables.scss";

.dialog-content-container {
  timeline-scope: --content__scrollcontainer;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
}

.content-area {
  position: relative;
  display: flex;
  flex-grow: 1;
  min-height: 0;
}

.content {
  overflow-y: auto;
  overscroll-behavior-y: none;
  scroll-timeline: --content__scrollcontainer y;
  flex-grow: 1;
  padding: 1.5rem;
}

.top-section,
.actions-section {
  position: sticky;
  padding: 0.8rem;
  display: flex;
  flex-direction: row;
  background: variables.$en-0;
  z-index: 10;
}

.top-section {
  top: 0;
  flex-direction: column;
  gap: 0.5rem;

  .title {
    font-size: 17px;
    color: variables.$btn-action-color;
    font-style: normal;
    font-weight: 200;
    line-height: normal;
  }

  .title-row {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
  }

  .header-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    margin-left: auto;
  }

  .close-icon-container {
    width: 24px;
    height: 24px;
    display: grid;
    place-items: center;
    border-radius: variables.$filter-button-border-radius;
    cursor: pointer;

    &:hover {
      background: #EAEEF1;
    }
  }
}

.has-scroll .top-section {
  animation: auto top-section-border linear forwards;
  animation-timeline: --content__scrollcontainer;
}

.no-scroll .top-section {
  border-bottom: 1px solid rgba(variables.$en-blue-2, 0.17);
}

.no-top-border .top-section {
  border-bottom: none !important;
}

.spacious .top-section,
.spacious .actions-section {
  padding: 1.25rem 1.5rem;
}

.no-top-border .actions-section {
  border-top: none !important;
}

.actions-section {
  bottom: 0;
  gap: .5rem;
  width: 100%;
  justify-content: flex-end;
}

.has-scroll .actions-section {
  animation: auto actions-section-border linear forwards;
  animation-timeline: --content__scrollcontainer;
}

.no-scroll .actions-section {
  border-top: 1px solid rgba(variables.$en-blue-2, 0.17);
}

// Lines of a sheet in the overlay slot, shown only while the header or actions line is hidden.
.content-area :deep([data-slot="sheet-content"]) {
  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    z-index: 1;
    height: 1px;
    background: rgba(variables.$en-blue-2, 0.17);
    pointer-events: none;
  }

  &::before {
    top: 0;
    height: 0.5rem;
    background: none;
    border-top: 1px solid rgba(variables.$en-blue-2, 0.17);
    border-top-left-radius: inherit;
  }

  // EnOverlaySheet reaches 1px under the actions bar.
  &::after {
    bottom: 1px;
    height: 0.5rem;
    background: none;
    border-bottom: 1px solid rgba(variables.$en-blue-2, 0.17);
    border-bottom-left-radius: inherit;
  }
}

.no-scroll .content-area :deep([data-slot="sheet-content"]) {
  &::before,
  &::after {
    opacity: 0;
  }
}

.has-scroll .content-area :deep([data-slot="sheet-content"]) {
  &::before {
    animation: auto sheet-top-border linear forwards;
    animation-timeline: --content__scrollcontainer;
  }

  &::after {
    animation: auto sheet-bottom-border linear forwards;
    animation-timeline: --content__scrollcontainer;
  }
}

.no-top-border .content-area :deep([data-slot="sheet-content"]) {
  &::before,
  &::after {
    opacity: 1;
    animation: none;
  }
}

@keyframes sheet-top-border {
  0% {
    opacity: 1;
  }

  1% {
    opacity: 0;
  }

  100% {
    opacity: 0;
  }
}

@keyframes sheet-bottom-border {
  0% {
    opacity: 0;
  }

  99% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
}

@keyframes top-section-border {
  0% {
    border-bottom: 1px solid rgba(variables.$en-blue-2, 0);
  }

  1% {
    border-bottom: 1px solid rgba(variables.$en-blue-2, 0.17);
  }

  100% {
    border-bottom: 1px solid rgba(variables.$en-blue-2, 0.17);
  }
}

@keyframes actions-section-border {
  0% {
    border-top: 1px solid rgba(variables.$en-blue-2, 0.17);
  }

  99% {
    border-top: 1px solid rgba(variables.$en-blue-2, 0.17);
  }

  100% {
    border-top: 1px solid rgba(variables.$en-blue-2, 0);
  }
}
</style>